/**
 * Centralized Route Definitions
 *
 * Single source of truth for all application paths. The site is German-only;
 * paths are returned unprefixed.
 */

import { BASE_URL } from "@/lib/config";

export const routes = {
  external: {
    sellerRefreshDashboard: "https://eu.posthog.com/project/164312/dashboard/966101",
  },
  home: "/",
  integrations: {
    monday: "/integrations/monday",
  },
  /** Personal overview (user-area). Reachable by permanent AND anonymous
   *  (guest) sessions — the page gates on `!user`, not `is_anonymous`, and
   *  renders a guest-framed variant for anonymous users. Guests are routed
   *  here after email-OTP login (see AuthFlow) instead of the homepage. */
  dashboard: "/dashboard",
  profile: "/profil",
  // No standalone /login page — the unified register-and-login screen
  // lives at /registrieren (renders <AuthFlow/> for both flows). Keeping
  // the `login` key for callers whose semantics are "send the user
  // somewhere they can log in".
  login: "/registrieren",
  messages: "/nachrichten",
  campaigns: {
    sellerRefresh: (id: string) => `/angebote-aktualisieren/${encodeURIComponent(id)}`,
  },

  offerings: {
    list: "/angebote",
    detail: (id: string) => `/angebote/${id}`,
    /** Post-submit edit surface — points at the sectioned /details
     *  workbench. The legacy `/angebote/[id]/bearbeiten` route still
     *  308-redirects here for inbound email/bookmark traffic. */
    edit: (id: string) => `/angebot/${id}/details`,
  },
  requests: {
    list: "/anfragen",
    detail: (id: string) => `/anfragen/${id}`,
    /** Points at /details (sectioned autosave). The legacy
     *  `/anfragen/[id]/bearbeiten` route 308-redirects here. */
    edit: (id: string) => `/anfrage/${id}/details`,
  },
  deals: {
    list: "/deals",
    detail: (id: string) => `/deals/${id}`,
  },
  // Public permalink routes (anonymous + token-based access)
  publicRequest: (id: string, token?: string) =>
    token ? `/anfrage/${id}?token=${token}` : `/anfrage/${id}`,
  publicOffering: (id: string, token?: string) =>
    token ? `/angebot/${id}?token=${token}` : `/angebot/${id}`,
  /** Land-check permalink. The funnel is decommissioned (ARCH-033 2.4) and this
   *  path now serves a static LandVest hand-off, but the route is retained so
   *  already-delivered emails and their `?token=` permalinks still resolve —
   *  see the SLUG_TO_TABLE note in lib/auth/token-paths.ts. */
  publicLandCheck: (id: string, token?: string) =>
    token ? `/flaechencheck/${id}?token=${token}` : `/flaechencheck/${id}`,

  // Post-submission "details" surface — re-entry point from the
  // welcome email and follow-up prompts. Dedicated enrichment page
  // (auto-save, no OTP). Unverified users hitting this URL get
  // bounced to /verify by the server component.
  detailsRequest: (id: string, token?: string) =>
    token ? `/anfrage/${id}/details?token=${token}` : `/anfrage/${id}/details`,

  // Email verification (guest submission flow). Token-bearing access
  // (e.g. from email permalinks) is handled by proxy.ts intercepting
  // ?token= and redirecting through /api/auth/redeem-token — callers
  // just emit the bare verify path, no `redeem(...)` helper needed.
  verify: {
    request: (id: string) => `/verify/anfrage/${id}`,
    offering: (id: string) => `/verify/angebot/${id}`,
  },
  accessRecovery: "/zugang-wiederherstellen",

  // Funnels
  schnellanfrage: "/schnellanfrage/standort",
  schnellangebot: "/schnellangebot/standort",
  flaechenPruefen: "/flaechen-pruefen",
  registrieren: "/registrieren",

  // Content pages
  content: {
    fourAxes: "/vier-ausgleichsarten",
    compensationTypes: "/kompensationsarten-im-ueberblick",
    /** Wissens-Hub — gruppierte Übersicht aller Wissens-Inhalte. */
    wissen: "/wissen",
    kontakt: "/kontakt",
    /**
     * Public, unlisted business-card profiles: QR/direct links only.
     * Keep out of site navigation, team cards and sitemap; pages emit noindex.
     * These are not private routes. Preserve the paths when team copy changes.
     */
    teamContact: (person: string) => `/k/${encodeURIComponent(person)}`,
    /** Permanent printed QR entry points; redirect to unlisted profiles with tracking. */
    teamQr: (person: string) => `/q/${encodeURIComponent(person)}`,
    /**
     * The key is the concept ("Preisindex", the label used in nav and footer);
     * the value is the page that actually serves it. `/preisindex` itself is a
     * legacy WordPress URL and is a 308 `source` in `lib/seo/redirects.ts` —
     * NOT a live route. Do not "correct" this to "/preisindex": that path has
     * no page behind it, so every nav and footer link would gain a needless
     * 308 hop. Registering an MDX page at that slug trips the no-shadow
     * invariant in `lib/seo/__tests__/redirects.test.ts` — but note that guard
     * reads the MDX registry only: an `app/preisindex/page.tsx` would be
     * shadowed by the redirect with no test catching it, and changing this
     * value alone is caught by nothing.
     */
    preisindex: "/aktueller-preis-pro-oekopunkt",
    wasSindOekopunkte: "/was-sind-oekopunkte",
    glossar: "/glossar",
    bundeslandUebersicht: "/oekokontoverordnungen-bundeslaender",
    ueberUns: "/ueber-uns",
    referenzen: "/referenzen",
    stiftung: "/stiftung",
    vorhabentraeger: "/vorhabentraeger",
    infrastrukturentwickler: "/infrastrukturentwickler",
    erneuerbareEnergienEntwickler: "/erneuerbare-energien-entwickler",
    // Für Vorhabenträger → Erneuerbare Energien (header menu)
    kompensationWindenergie: "/erneuerbare-energien/windenergie",
    kompensationPhotovoltaik: "/erneuerbare-energien/photovoltaik",
    kompensationBatteriespeicher: "/erneuerbare-energien/batteriespeicher",
    kommune: "/kommune",
    wasSindKompensationsmassnahmen: "/was-sind-kompensationsmassnahmen",
    ausgleichsflaechen: "/ausgleichsflaechen",
    forstwirt: "/forstwirt",
    landVerpachten: "/land-verpachten",
    leistungen: "/leistungen",
    flaecheAnbieten: "/flaeche-anbieten",
    // Maßnahmen → Artenschutz (header menu)
    artenschutz: "/artenschutz",
    cefMassnahmen: "/artenschutz/cef",
    fcsMassnahmen: "/artenschutz/fcs",
    // Maßnahmen → Ausgleichsflächen (header menu)
    biotopausgleich: "/ausgleichsflaechen/biotopausgleich",
    ackerlandStilllegung: "/ausgleichsflaechen/ackerland-stilllegung",
    forstausgleichWaldumwandlung: "/ausgleichsflaechen/forstausgleich-waldumwandlung",
    landschaftsbildErholung: "/ausgleichsflaechen/landschaftsbild-erholung",
    // Naturraum / Kompensationsraum cluster
    naturraumPlz: "/naturraum-bestimmen-postleitzahl",
    bfnNaturraum: "/bfn-naturraum-d-code-haupteinheiten",
    kompensationsraumVsNaturraum: "/kompensationsraum-vs-naturraum",
    eingriffsregelungVergleich: "/eingriffsregelung-bundeslaender-vergleich",
    nrwKompensationsraeume: "/kompensationsraeume-nrw-k01-k05",
    brandenburgLapro: "/naturraeume-brandenburg-laproprogramm",
    berlinNaturraumBindung: "/kompensationsraum-berlin-naturraum-bindung",
    // Recht & Sicherung — hub + per-Bundesland spokes (BW/BY/NRW geplant)
    dinglicheSicherung: "/dingliche-sicherung-oekopunkte",
    /** Per-Bundesland Spoke (e.g. /dingliche-sicherung-oekopunkte-niedersachsen). */
    dinglicheSicherungIn: (bundesland: string) => `/dingliche-sicherung-oekopunkte-${bundesland}`,
    // Site structure (consultant's sheet, final 2026-10-21). Parents and
    // breadcrumbs live in lib/site-structure, not here.
    kompensationsmassnahmen: "/kompensationsmassnahmen",
    flaecheneigentuemer: "/flaecheneigentuemer",
    oekopunkte: "/oekopunkte",
    oekopunkteKaufen: "/oekopunkte-kaufen",
    oekopunkteVerkaufen: "/oekopunkte-verkaufen",
    oekopunkteVermittlung: "/oekopunkte-vermittlung",
    flaechenvermittlung: "/flaechenvermittlung",
    rechtlicheAbsicherung: "/rechtliche-absicherung-und-vertraege",
    behoerdenabstimmung: "/behoerdenabstimmung",
    oekologischeBaubegleitung: "/oekologische-baubegleitung",
    landschaftspflegerischerBegleitplan: "/landschaftspflegerischer-begleitplan",
    kompensationAnfragen: "/kompensation-anfragen",
    kompensationsberatung: "/kompensationsberatung",
    erneuerbareEnergien: "/erneuerbare-energien",
    flaecheBewertenLassen: "/flaeche-bewerten-lassen",
    potenzialanalyseFlaeche: "/potenzialanalyse-flaeche",
    wasSindAusgleichsflaechen: "/was-sind-ausgleichsflaechen",
    // Existing articles that the site tree places (no other code linked them yet).
    batteriespeicher: "/batteriespeicher",
    checkPvFlaechen: "/check-pv-flaechen-welche-eignen-sich",
    flaechenverbrauch: "/flaechenverbrauch-oekologisch-entschaefen",
    freiflaechenVerkaufenOderVerpachten:
      "/freiflaechen-verkaufen-oder-verpachten-vor-und-nachteile",
    freiflaechenRueckbau: "/freiflaechenanlagen-rueckbau-und-recycling",
    jagd: "/jagd",
    alternativeEinnahmequellen: "/alternative-einnahmequellen",
    naturvertraeglichePv: "/naturvertraegliche-photovoltaik-freiflaechenanlagen-kriterien",
    netzbetreiber: "/netzbetreiber",
    photovoltaikFaq: "/photovoltaik-faq",
    pvAufPachtflaechen: "/photovoltaik-freiflaechen-auf-pachtflaechen-alle-schritte-im-ueberblick",
    pvOderWindkraft: "/photovoltaik-oder-windkraft-was-eignet-sich-mehr-fuer-die-freiflaeche",
    projektentwickler: "/projektentwickler",
    pvAnAutobahnen: "/pv-an-autobahnen-und-bahnstrecken",
    solarInvestment: "/solar-investment-fuer-minderwertige-boeden",
    solarparkBauen: "/solarpark-bauen",
    solarparkGemeinde: "/solarpark-gemeinde-rolle",
    wieGrossSolarpark: "/wie-gross-muss-ein-solarpark-sein",
    wieGrossPv: "/wie-gross-sollte-die-pv-sein",
    windenergieFaqAnlagenbetreiber: "/windenergie-faq-fuer-anlagenbetreiber",
    windenergieFaqFlaecheneigner: "/windenergie-faq-fuer-flaecheneigner",
    windenergieGenehmigung: "/windenergie-genehmigung",
    windenergieLandschaftsbild: "/windenergie-landschaftsbild",
    impressum: "/impressum",
    datenschutz: "/datenschutz",
    agb: "/agb",
    agbProvision: "/agb-provision",
    widerrufsbelehrung: "/widerrufsbelehrung",

    // Additional content pages referenced by MDX content (kept here so
    // content cross-links go through the same registry). Slugs match
    // the MDX filenames in `src/content/pages/okopunkte/de/`.
    dasSystemOekopunkte: "/das-system-oekopunkte",
    informationenFuerGemeinden: "/informationen-fuer-gemeinden",
    oekopunkteKaufenVerkaufen: "/oekopunkte-kaufen-verkaufen",
    oekopunkteUmwelt: "/oekopunkte-umwelt",
    ausgleichsflaechenOekopunkte: "/ausgleichsflaechen-oekopunkte",
    oekopunkteImSaarland: "/oekopunkte-im-saarland",
    agrarpacht: "/agrarpacht",
    agrarpachtPreis: "/agrarpacht-preis",
    agrarpachtSteuererklaerung: "/agrarpacht-steuererklaerung",
    agriPv: "/agri-pv",
    jagdpachtBerechnung: "/wie-wird-die-jagdpacht-berechnet",
    jagdpachtPreise: "/jagdpacht-preise",
    jagdpachtSteuer: "/jagdpacht-steuer",
    landVerpachtenFuerSolarpark: "/land-verpachten-fuer-solarpark",
    landVerpachtenSteuer: "/land-verpachten-steuer",
    pachtvertraege: "/pachtvertraege-worauf-sollte-geachtet-werden",
    photovoltaikProHektar: "/photovoltaik-pro-hektar",
    pvFreiflaechenVoraussetzung: "/pv-freiflaechenanlagen-voraussetzung",
    pvPlatzbedarfPachtpreise:
      "/pv-freiflaechenanlagen-platzbedarf-einfluss-auf-bodenmarkt-und-pachtpreise",
    waldVerpachten: "/wald-verpachten",
    windenergieNaturArtenschutz: "/windenergie-natur-und-artenschutz",
    windkraftanlagenAufDemAcker: "/windkraftanlagen-auf-dem-acker",
    windkraftanlagenImWald: "/windkraftanlagen-im-wald",
    /** Per-Bundesland landing page (e.g. /oekopunkte-in-bayern). */
    oekopunkteIn: (bundesland: string) => `/oekopunkte-in-${bundesland}`,
  },

  organization: {
    dashboard: "/organization",
    users: "/organization/users",
    settings: "/organization/settings",
    /** Owner-only Eigentümer-Bereich (ST6). */
    eigentuemerBereich: "/organization/eigentuemer-bereich",
    projects: {
      list: "/organization/projects",
      new: "/organization/projects/new",
      import: "/organization/projects/import",
      detail: (id: string) => `/organization/projects/${id}`,
      edit: (id: string) => `/organization/projects/${id}/edit`,
    },
    requests: {
      list: "/organization/requests",
      new: "/organization/requests/new",
      detail: (id: string) => `/organization/requests/${id}`,
      edit: (id: string) => `/organization/requests/${id}/edit`,
    },
  },

  // Data-check (datencheck) is the campaign-driven self-service flow that
  // backs targeted email campaigns. Public surface; the campaign id is
  // included in the URL.
  dataCheck: (campaignId: string) => `/datencheck/${campaignId}`,

  // OAuth authorization-code consent screen. The only non-admin page in the
  // app that hosts a Server Action, which is why it is named here and in
  // SERVER_ACTION_ROUTES (see lib/server-actions/action-shaped-post.ts).
  oauth: {
    consent: "/oauth/consent",
  },

  admin: {
    dashboard: "/admin/dashboard",
    sales: "/admin/sales",
    activity: "/admin/activity",
    // Konfiguration
    plz: "/admin/plz",
    naturraeume: "/admin/naturraeume",
    instruments: "/admin/instruments",
    registry: "/admin/registry",
    priceLists: "/admin/price-lists",
    emails: "/admin/emails",
    // Entwicklung / Tools
    matchDebugger: "/admin/match-debugger",
    processLab: "/admin/prozesslabor",
    events: "/admin/events",
    leadAgent: {
      list: "/admin/lead-agent",
      detail: (runId: string) => `/admin/lead-agent/${encodeURIComponent(runId)}`,
      /** One case (a Monday lead): its runs in order, their cost and its Teams thread. */
      case: (leadItemId: string) => `/admin/lead-agent/fall/${encodeURIComponent(leadItemId)}`,
    },
    offerings: {
      list: "/admin/offerings",
      detail: (id: string) => `/admin/offerings/${id}`,
      /** Every Treffer of one row of the Angebot, filterable: the needs it may serve. */
      treffer: (id: string, rowId: string) =>
        `/admin/offerings/${encodeURIComponent(id)}/treffer/${encodeURIComponent(rowId)}`,
    },
    requests: {
      list: "/admin/requests",
      detail: (id: string) => `/admin/requests/${id}`,
      /** Every Treffer of one row of the Anfrage, filterable (Bedarf für Sales). */
      treffer: (id: string, rowId: string) =>
        `/admin/requests/${encodeURIComponent(id)}/treffer/${encodeURIComponent(rowId)}`,
    },
    deals: {
      list: "/admin/deals",
      detail: (id: string) => `/admin/deals/${id}`,
      opportunities: "/admin/deals/opportunities",
    },
    users: {
      list: "/admin/users",
      detail: (id: string) => `/admin/users/${id}`,
    },
    organizations: {
      list: "/admin/organizations",
      detail: (id: string) => `/admin/organizations/${id}`,
    },
    campaigns: {
      list: "/admin/campaigns",
      detail: (id: string) => `/admin/campaigns/${id}`,
    },
  },
  /**
   * Server API endpoints (fetch URLs, not navigation paths) — centralized so
   * client fetches reference the path from one source. Collection endpoints only;
   * the caller appends an id / query string as needed.
   */
  api: {
    integrations: {
      monday: {
        root: "/api/integrations/monday",
        config: "/api/integrations/monday/config",
        connect: "/api/integrations/monday/oauth/connect",
        disconnect: "/api/integrations/monday/oauth/disconnect",
        lifecycle: "/api/integrations/monday/lifecycle",
        callback: "/api/integrations/monday/oauth/callback",
        /** The records of a Monday item; the caller appends `?boardId=…&itemId=…`. */
        records: "/api/integrations/monday/records",
        section: (kind: string, id: string, section: string) =>
          `/api/integrations/monday/records/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/sections/${encodeURIComponent(section)}`,
        /** A record's form choices; the caller appends the item and the location as the query. */
        options: (kind: string, id: string) =>
          `/api/integrations/monday/records/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/options`,
        /** What covers a linked record's rows; the caller appends the item as the query. */
        coverage: (kind: string, id: string) =>
          `/api/integrations/monday/records/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/coverage`,
      },
    },
    emailPreview: (template: string) => `/api/dev/email-preview/${encodeURIComponent(template)}`,
    /** Where a PLZ lies: Bundesland, Kreis and Naturraum options (404 for an unknown PLZ). */
    /** The axis registry: axes, their units and subjects (public reference data). */
    registry: "/api/registry",
    plzMetadata: (plz: string) => `/api/metadata/plz/${encodeURIComponent(plz)}`,
    user: {
      /** Adding or replacing the login phone from the profile (ARCH-055). */
      loginPhone: {
        stepUp: "/api/user/login-phone/step-up",
        verifyStepUp: "/api/user/login-phone/step-up/verify",
        confirm: "/api/user/login-phone/confirm",
      },
    },
    sellerRefresh: (id: string) => `/api/campaigns/${encodeURIComponent(id)}/seller-refresh`,
    /** Public contact download used by the unlisted business-card profiles. */
    teamVCard: (person: string) => `/api/team/${encodeURIComponent(person)}/vcard`,
    admin: {
      processLab: (file: string) =>
        `/api/admin/process-lab/${file.split("/").map(encodeURIComponent).join("/")}`,
      campaigns: {
        sellerRefreshSummary: (id: string) =>
          `/api/admin/campaigns/${encodeURIComponent(id)}/seller-refresh-summary`,
        prepare: (id: string) => `/api/admin/campaigns/${encodeURIComponent(id)}/prepare`,
        /** POST = send (or resume) a campaign; the server decides who is still sendable. */
        send: (id: string) => `/api/admin/campaigns/${encodeURIComponent(id)}`,
      },
      deals: "/api/admin/deals",
      leadAgent: {
        /** GET = a page of runs (`?status=&kind=&leadItemId=&page=&limit=`); POST = start a run. */
        runs: "/api/admin/lead-agent/runs",
        /** GET = one case: a page of its runs (`?page=&limit=`), totals and thread log. */
        case: (leadItemId: string) =>
          `/api/admin/lead-agent/cases/${encodeURIComponent(leadItemId)}`,
        run: (runId: string) => `/api/admin/lead-agent/runs/${encodeURIComponent(runId)}`,
        /** GET = a short-lived download URL for the run's transcript. */
        transcript: (runId: string) =>
          `/api/admin/lead-agent/runs/${encodeURIComponent(runId)}/transcript`,
        totals: "/api/admin/lead-agent/totals",
        /** GET = the drafts awaiting approval. */
        drafts: "/api/admin/lead-agent/drafts",
        /** GET = the stop switch; PUT = set it. */
        control: "/api/admin/lead-agent/control",
      },
      dealEmailPreview: "/api/admin/deals/email-preview",
      dealVisibility: (id: string) => `/api/admin/deals/${id}/visibility`,
      /** GET = each party's "deal started" mail. */
      dealNotifications: (id: string) => `/api/admin/deals/${id}/notifications`,
      /** POST = send unclear or failed ones again. */
      dealNotificationsResend: (id: string) => `/api/admin/deals/${id}/notifications/resend`,
      offerings: "/api/admin/offerings",
      offeringSection: (id: string, section: string) =>
        `/api/admin/offerings/${encodeURIComponent(id)}/sections/${encodeURIComponent(section)}`,
      /** The Angebot form's choices; the caller appends `?plz=&bundesland=&naturraum_id=`. */
      offeringFormOptions: (id: string) =>
        `/api/admin/offerings/${encodeURIComponent(id)}/form-options`,
      /** What covers each row of the Angebot: its deals and evaluated demand. */
      offeringCoverage: (id: string) => `/api/admin/offerings/${encodeURIComponent(id)}/coverage`,
      /** A page of one row's Treffer; the caller appends the filters (`trefferQueryString`). */
      offeringRowTreffer: (id: string, rowId: string) =>
        `/api/admin/offerings/${encodeURIComponent(id)}/rows/${encodeURIComponent(rowId)}/treffer`,
      /** GET = the Monday item the Angebot's lead went to, or null. */
      offeringCrmLink: (id: string) => `/api/admin/offerings/${encodeURIComponent(id)}/crm-link`,
      organizations: "/api/admin/organizations",
      requests: "/api/admin/requests",
      requestSection: (id: string, section: string) =>
        `/api/admin/requests/${encodeURIComponent(id)}/sections/${encodeURIComponent(section)}`,
      /** The Anfrage form's choices; the caller appends `?plz=&bundesland=&naturraum_id=`. */
      requestFormOptions: (id: string) =>
        `/api/admin/requests/${encodeURIComponent(id)}/form-options`,
      /** What covers each row of the Anfrage: its deals, and each row's Treffer and best three. */
      requestCoverage: (id: string) => `/api/admin/requests/${encodeURIComponent(id)}/coverage`,
      /** A page of one row's Treffer; the caller appends the filters (`trefferQueryString`). */
      requestRowTreffer: (id: string, rowId: string) =>
        `/api/admin/requests/${encodeURIComponent(id)}/rows/${encodeURIComponent(rowId)}/treffer`,
      /** GET = the Monday item the request's lead went to, or null. */
      requestCrmLink: (id: string) => `/api/admin/requests/${encodeURIComponent(id)}/crm-link`,
      users: "/api/admin/users",
    },
  },
} as const;

/** Ensures a leading slash. The site is German-only — no locale prefix. */
export function localePath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Append query params to a path, picking the right separator (`?` if the
 * path has no query yet, `&` otherwise). Skips entries whose value is
 * `undefined`; an array value appends one pair per element, so a page's
 * `searchParams` can be forwarded as-is. Use for one-shot CTAs
 * (`?verified=true`, `?source=submit`) and for redirects that must keep the
 * incoming query — for anything more complex, build a URLSearchParams.
 */
export function withQuery(
  path: string,
  params: Record<string, string | string[] | undefined>
): string {
  const pairs = Object.entries(params).flatMap(([key, value]) =>
    (value === undefined ? [] : Array.isArray(value) ? value : [value]).map(
      (item): [string, string] => [key, item]
    )
  );
  if (pairs.length === 0) return path;
  const separator = path.includes("?") ? "&" : "?";
  const query = pairs
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return `${path}${separator}${query}`;
}

/** Full absolute URL (BASE_URL + path). For emails, Teams, external links. */
export function absoluteUrl(path: string): string {
  return `${BASE_URL}${localePath(path)}`;
}
